import {Report} from '../../models-old/ngp-report/ngp-data-types';
import {StockItem} from '../../models-old/datastructures';
import {
  CellClickedEvent,
  CellKeyDownEvent,
  ColDef,
  GridApi,
  ICellEditorParams,
  ICellRendererParams,
  IRowNode,
  RowSelectedEvent,
} from 'ag-grid-community';
import {
  GridColumnHeaderComponent,
} from '../../../shared-modules/shared-grid/components/grid-column-header/grid-column-header.component';
import {DataAdditionalHeaderMenuOption} from '../../models-old/ngp-report-grid/data-additional-header-menu-option';
import {StoreObject} from '../../models-old/store/store-object';
import {IStore} from '../../../shared/shared-models/store/store';
import {NGPReport} from '../../models-old/ngp-reports/ngp-report';
import {
  updateAllNGPReports,
  updateIsSelectedForSingleNGPReport,
  updateSingleNGPReport,
} from '../../../features/ngp-report/store/ngp-report.actions';
import {Store} from '@ngrx/store';
import {IDepartment, ISubDepartment} from '../../../shared/shared-models/stock/departments';
import {setEditedItem} from '../../../features/stock-manager/store/stock-manager.actions';
import {stockManagerHasEditableFieldChanges} from '../../../features/stock-manager/utils/stock-manager-update.utils';
import {ensureDepartmentNameFields} from '../../../shared/shared-utils/stock/department-display.utils';
import {OriginalValue} from '../../../features/stock-manager/store/stock-manager.reducer.utils';
import {stockManagerGridColDefs} from '../../../features/stock-manager/models/stock-manager-grid-col-def';
import {isEqual} from 'lodash';
import {ngpReportGridColDefs} from '../../models-old/ngp-report-grid/defaults/ngp-report/ngp-report-grid-col-defs';
import {TLineColour} from '../../../features/settings-new/models';
import {applyLineColoursToStockManager as applyStockLineColours} from '../../../features/stock-manager/utils/stock-manager-line-colour.utils';
import {decodeOnHoldCode} from '../../../shared/shared-module-ui/utils/on-hold-code/on-hold-code.utils';
import {stockMangerVisibleFieldsDefault} from '../../../features/stock-manager/models/stock-manager-grid-options';

/**
 * @class GridUtils
 * Utilities for manipulating and displaying the grid and its data.
 * This includes cell renderer functions.
 *
 * @member verifyArrayDataExists Verify if there is data provided in the array or return an empty array.
 * @member mapStoreObjectToStore Map store objects to an array of stores.
 * @member mapReportsToNGPReportsArray Map NGP Reports to an Array from a Reports Object
 * @member applyLineColoursToNgpReports Apply the line colours to the NGP Report comparing a colour code and line colour hash code.
 * @member setColumnTypeToEditable Set column definitions types to edit as a style if they are editable.
 * @member addColumnTypeCellEdit Add the column type of `cellEdit` to a column definition.
 * @member setColumnHeaderMenu Set the column header menu component and additional parameters for column definitions.
 * @member getHumanReadableColumnIdentifier Get a human-readable identifier for a column definition field.
 */
export class GridUtils {

  /**
   * Verify if there is data provided in the array.
   * If there is no data it will return an empty array,
   *
   * @param data An array of data that needs to be verified.
   * @returns An array of the same data or an empty array if no data was provided.
   */
  static verifyArrayDataExists<T>(data: T[]): T[] {
    return data?.length > 0 ? data : [];
  }

  /**
   * Map store objects to an array of stores.
   *
   * @member {StoreObject} storeObject A store object that needs to be converted to an array.
   * @returns Returns an array of stores. The testing store will always be returned first.
   */
  static mapStoreObjectToStore(storeObject: StoreObject): IStore[] {
    const stores: IStore[] = [];
    if (storeObject?.order?.length < 1) {
      return [];
    }
    storeObject.order.forEach((storeId: string, index: number): void => {
      stores.push({sequence: index, storeId: storeId, name: storeObject.stores[storeId].name} as IStore);
    });
    stores.sort((a: IStore, b: IStore): number => a.name < b.name ? -1 : 1);
    return stores;
  }

  /**
   * Map NGP Reports to an Array from a Reports Object
   *
   * @param reportObject An object containing reports as properties.
   * @returns An array of NGP Report objects.
   */
  static mapReportsToNGPReportsArray(reportObject: { [reportId: string]: Report }): NGPReport[] {
    const reportReturn: NGPReport[] = [];
    Object.keys(reportObject).forEach((reportId: string): void => {
      const report: Report = reportObject[reportId];
      reportReturn.push({
        ...report,
      } as NGPReport);
    });
    return [...reportReturn];
  }

  /**
   * Apply the line colours to the NGP Report comparing a colour code and line colour hash code.
   * The following properties are added: `lineColourDescription` and `lineColourValue`.
   *
   * @param lineColours Line colours with their code and has code assigned.
   * @param ngpReports The NGP Report to add the properties for line colours to.
   * @returns An array of NGP Report objects.
   */
  static applyLineColoursToNgpReports(lineColours: TLineColour, ngpReports: NGPReport[]): NGPReport[] {
    const reports: NGPReport[] = [];
    ngpReports.forEach((r: NGPReport): void => {
      const newR = {...r};
      if (newR.lineColourCode && newR.lineColourCode > 0) {
        const color = lineColours[newR.lineColourCode];
        if (color) {
          newR.lineColourDescription = color.description;
          newR.lineColourValue = color.value;
        }
      }
      reports.push(newR);
    });
    return reports;
  }

  /**
   * Apply the line colours to the Stock Manager Page comparing a colour code and line colour hash code.
   * The following properties are added: `lineColourDescription` and `lineColourValue`.
   *
   * @param lineColour Line colours with their code and has code assigned.
   * @param stockItems The Stock Items to add the properties for line colours to.
   * @returns An array of Stock Item objects.
   */
  static applyLineColoursToStockManager(lineColour: TLineColour, stockItems: StockItem[]): StockItem[] {
    return applyStockLineColours(lineColour, stockItems);
  }

  /**
   * Set column definitions types to edit as a style if they are editable.
   *
   * @param colDefs Grid column definitions.
   * @returns An array of Column Definitions objects.
   */
  static setColumnTypeToEditable(colDefs: ColDef[]): ColDef[] {
    if (colDefs?.length > 0) {
      colDefs.forEach((col: ColDef): void => {
        if (col.editable) {
          col.type = this.changeColumnDefinitionType(col, 'cellNormal', 'cellEdit');
        }
      });
    }
    return colDefs || [];
  }

  /**
   * Add the column type of `cellEdit` to a column definition.
   *
   * @param colDef The column definition the type `cellEdit` is being added to.
   * @returns An array of `type` objects.
   */
  static addColumnTypeCellEdit(colDef: ColDef): string[] {
    if (!colDef.type) {
      colDef = {...colDef, type: []};
    }
    const cellEditTypeIndex: number = [...colDef.type]?.findIndex((type: string): boolean => type === 'cellEdit') || -1;
    if (cellEditTypeIndex < 0) {
      return [...colDef.type || [], 'cellEdit'];
    }
    return [...colDef.type];
  }

  /**
   * Add and/or remove column types from column definitions.
   *
   * @param colDef The column definition to amend types for.
   * @param deleteType The type that needs to be removed from the column definition.
   * @param addType ? The type that needs to be added to the column definition.
   * @returns An array of `type` objects.
   */
  static changeColumnDefinitionType(colDef: ColDef, deleteType: string, addType?: string): string[] {
    if (!colDef.type) {
      return this.addColumnTypeCellEdit(colDef);
    }
    const updatedTypes = [...colDef.type];
    const deleteIndex = updatedTypes.findIndex(type => type === deleteType);

    if (deleteIndex > -1) {
      updatedTypes.splice(deleteIndex, 1);
    }
    if (addType && !updatedTypes.includes(addType)) {
      updatedTypes.push(addType);
    }
    colDef.type = updatedTypes;
    return updatedTypes;
  }


  /**
   * Set the column header menu component and additional parameters for column definitions.
   *
   * @param colDefs The column definitions to have the header menu component added to.
   * @param headerComponentParamData The data to be sent through to the header menu component.
   * @returns An array of column definitions with Header Menu's added.
   */
  static setColumnHeaderMenu<ActionReturnType>(colDefs: ColDef[], headerComponentParamData: DataAdditionalHeaderMenuOption<ActionReturnType>[]): ColDef[] {
    colDefs.forEach((col: ColDef): void => {
      col.headerComponent = GridColumnHeaderComponent;
      col.headerComponentParams = [...headerComponentParamData];
    });
    return colDefs;
  }

  /**
   * Get a human-readable identifier for a column definition field.
   *
   * @Example
   * If (col headerName) return col headerName;
   * else convert col field from `propertyName` to `Property Name`
   *
   * @param col The column definition.
   * @returns A human-readable identifier for the column definition.
   */
  static getHumanReadableColumnIdentifier(col: ColDef): string {
    if (col.headerName) {
      return col.headerName;
    } else {
      const splitWords: string = col.field.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
      return splitWords.replace(/\b\w/g, char => char.toUpperCase());
    }
  }

  static updateIsEditedAndForceRefresh(params: ICellEditorParams | ICellRendererParams): void {
    // Validate that originalValue exists and is properly structured
    if (!params.data.originalValue || typeof params.data.originalValue !== 'object') {
      console.error(`Invalid originalValue structure with value ${params.data.originalValue}`);
      return;
    }
    const originalValue = params?.data?.originalValue as OriginalValue<StockItem | NGPReport>;
    const fieldsToIgnore = new Set(['isEdited', 'isSelected', 'error', 'newlyAdded', 'isError']);
    const isStockManagerGrid = Boolean((params.context as { isStockManager?: boolean } | undefined)?.isStockManager);

    const isAnyEdited = isStockManagerGrid
      ? stockManagerHasEditableFieldChanges(params.data as StockItem)
      : Object.keys(originalValue).some((key: string) => {
        if (fieldsToIgnore.has(key)) {
          return false;
        }
        const originalFieldValue = params?.data?.originalValue?.[key]?.value;
        const currentValue = params?.data?.[key];

        return !isEqual(originalFieldValue, currentValue);
      });

    const rowNode = params.api.getRowNode(params.node.id);
    void Promise.resolve().then(() => {
      if (rowNode) {
        const updatedData = {...params.data, isEdited: isAnyEdited};
        rowNode.setData(updatedData);
        if (isStockManagerGrid) {
          params.api.refreshCells({
            rowNodes: [rowNode],
            columns: ['icons'],
            force: true,
          });
        }
      }
    });

  }


  static updateErrorAndForceRefresh(params: ICellEditorParams | ICellRendererParams, departments: IDepartment[]): void {
    const isError = this.checkSubDepCode(params, departments);
    const rowNode = params.api.getRowNode(params.node.id);
    void Promise.resolve().then(() => {
      if (rowNode) {
        let updatedData = {};
        updatedData = {...params.data, error: isError, isError: isError};
        rowNode.setData(updatedData);
      }
    });

  }


  static updateErrorAndForceRefreshForOnHoldCode(params: ICellEditorParams | ICellRendererParams, code: number): void {
    const isError = decodeOnHoldCode(+code) === null;

    const rowNode = params.api.getRowNode(params.node.id);
    if (rowNode) {
      let updatedData = {};
      if (isError) {
        updatedData = {...rowNode.data, error: isError};
      } else {
        updatedData = {...rowNode.data, error: isError};
      }
      void Promise.resolve().then(() => {
        rowNode.setData(updatedData);
      });
    }
  }

  static checkSubDepCode(params: ICellEditorParams | ICellRendererParams, departments: IDepartment[]): boolean {
    if (params.colDef.field === 'dep') {
      if (params.data.dep === '0') {
        return true;
      } else if (params.data.dep === params.data.originalValue.dep.value) {
        const department = departments.find((dep: IDepartment): boolean => dep.department === params.data.dep);
        const subDepartments = department?.subDepartments;
        if (subDepartments) {
          return !(subDepartments.filter((subDep: ISubDepartment): boolean => subDep.subDepartment === params.data.subDep).length > 0);
        }
        return false;
      }
    }
    if (params.data.dep !== '0') {
      const department = departments.find((dep: IDepartment): boolean => dep.department === params.data.dep);
      const subDepartments = department?.subDepartments;
      if (subDepartments) {
        return !(subDepartments.filter((subDep: ISubDepartment): boolean => subDep.subDepartment === params.data.subDep).length > 0);
      }
    }
    return false; // No error for other fields
  }

  static async removeIsEditingFlagFromNgpItem(ngpReportParams: NGPReport, field: string, store: Store): Promise<void> {
    const updatedNgpItem: NGPReport = {
      ...ngpReportParams,
      [field]: ngpReportParams.originalValue[field].value,
      isEdited: false,
      isSelected: ngpReportParams.isSelected,
    };

    store.dispatch(updateSingleNGPReport({ngpReport: updatedNgpItem}));
  }

  static removeIsEditingFlagAndDispatch(params: NGPReport | StockItem, field: string, store: Store, currentPage: string): void {
    if (currentPage === 'ngp-report') {
      void GridUtils.removeIsEditingFlagFromNgpItem(params as NGPReport, field, store);
    } else {
      const originalFieldValue: boolean | number | string | string[] | Date = params.originalValue?.[field]?.value ?? '';
      params[field] = originalFieldValue;
      const stockItem = {
        ...params,
        originalValue: {
          ...params.originalValue,
          [field]: {value: originalFieldValue},
        },
      } as StockItem;
      store.dispatch(setEditedItem({stockItem}));
    }
  }

  static updateSingleNgpItem(params: NGPReport, field: string, newValue: string | number | boolean | string[], store: Store): void {
    const updatedNgpItem: NGPReport = {
      ...params, [field]: newValue, isEdited: params[field] !== params.originalValue[field].value,
    };
    store.dispatch(updateSingleNGPReport({ngpReport: updatedNgpItem}));
  }

  static updateSingleItem(params: NGPReport | StockItem, field: string, newValue: string | number | boolean | string[], store: Store, currentPage: string): void {
    if (currentPage === 'ngp-report' && params?._tags?.includes('disabled')) {
      return;
    }
    if (currentPage === 'ngp-report') {
      GridUtils.updateSingleNgpItem(params as NGPReport, field, newValue, store);
      return;
    }
    if (params['_tags']?.[0] === '' && params['_tags']?.length === 1) {
      params['_tags'] = [];
    }
    params.isEdited = stockManagerHasEditableFieldChanges(params as StockItem);
    store.dispatch(setEditedItem({stockItem: params as StockItem}));
  }

  static updateIsSelectedForSingleNgpItem(cellKeyDownEvent: CellKeyDownEvent | CellClickedEvent | RowSelectedEvent, selectedValue: boolean, store: Store): void {
    let rowNode;

    if (cellKeyDownEvent.type) {
      rowNode = cellKeyDownEvent.node.data;
    } else {
      rowNode = cellKeyDownEvent;
    }
    const updatedNgpItem: NGPReport = {
      ...rowNode, isSelected: selectedValue,
    };
    store.dispatch(updateIsSelectedForSingleNGPReport({ngpReport: updatedNgpItem}));
  }

  static updateIsSelectedForSingleNgpItemOnEnter(ngpReport: NGPReport, selectedValue: boolean, store: Store): void {
    const updatedNgpItem: NGPReport = {
      ...ngpReport, isSelected: selectedValue,
    };

    store.dispatch(updateSingleNGPReport({ngpReport: updatedNgpItem}));
  }

  static updateIsSelectedForDisplayedNgpItem(
    gridApi: GridApi,
    selectedValue: boolean,
    store: Store,
  ): void {
    if (gridApi.isDestroyed()) {
      return;
    }
    const pageSize = gridApi.paginationGetPageSize();
    const startingPoint = gridApi.paginationGetCurrentPage() * pageSize;
    const displayedRows = Array.from({length: pageSize}, (_, index) => {
      const node: IRowNode<NGPReport> = gridApi.getDisplayedRowAtIndex(startingPoint + index);
      if (node) {
        node.data.isSelected = selectedValue;
        return node.data;
      } else {
        return undefined;
      }
    }).filter((item): item is NGPReport => item !== undefined);


    store.dispatch(updateAllNGPReports({ngpReports: displayedRows}));
  }


  static disableItem(ngpReport: NGPReport, store: Store): void {
    store.dispatch(updateSingleNGPReport({ngpReport: {...ngpReport}}));
  }

  static getVisibleFieldKeysFromColDefs(colDefs: ColDef[]): string[] {
    return colDefs
      .filter((col: ColDef) => col.field && (col.lockVisible || col.hide !== true))
      .map((col: ColDef) => col.field);
  }

  static convertColumnDefinitionsToSelectPopover(
    colDefs: ColDef[],
    visibleFields: { [key: string]: boolean },
    visibleColumnOrder?: string[],
  ): { order: string[]; selection: { [key: string]: string }; disabledKeys: string[]; value: string[] } {
    const selectPopover = {order: [], selection: {}, disabledKeys: [], value: []};
    const isVisible = {...visibleFields};
    const selected = [];
    const unselected = [];
    const disabled = [];

    colDefs.forEach((col: ColDef) => {
      const description: string = GridUtils.getHumanReadableColumnIdentifier(col);

      if (col.lockVisible && col.field !== 'disabled') {
        disabled.push(col.field);
      } else if (Object.keys(isVisible).length > 0) {
        if (isVisible[col.field]) {
          selected.push(col.field);
        } else {
          unselected.push(col.field);
        }
      } else {
        if (!col.hide) {
          selected.push(col.field);
        } else {
          unselected.push(col.field);
        }
      }

      selectPopover.selection = {...selectPopover.selection, [col.field]: description};
    });

    const sortByDescription = (a: string, b: string): number => {
      const descriptionA: string = selectPopover.selection[a]?.toLowerCase() || '';
      const descriptionB: string = selectPopover.selection[b]?.toLowerCase() || '';
      return descriptionA.localeCompare(descriptionB);
    };

    if (visibleColumnOrder?.length) {
      const orderIndex = new Map<string, number>(
        visibleColumnOrder.map((field: string, index: number) => [field, index]),
      );
      const sortBySavedOrder = (a: string, b: string): number => {
        const indexA = orderIndex.has(a) ? orderIndex.get(a) : Number.MAX_SAFE_INTEGER;
        const indexB = orderIndex.has(b) ? orderIndex.get(b) : Number.MAX_SAFE_INTEGER;
        if (indexA !== indexB) {
          return indexA - indexB;
        }
        return sortByDescription(a, b);
      };
      selected.sort(sortBySavedOrder);
      unselected.sort(sortByDescription);
      disabled.sort(sortBySavedOrder);

      const visibleFromOrder = visibleColumnOrder.filter((field: string) => selected.includes(field));
      const selectedNotInOrder = selected.filter((field: string) => !visibleFromOrder.includes(field));
      const lockedNotInOrder = disabled.filter((field: string) => !visibleFromOrder.includes(field));
      selectPopover.value = [...visibleFromOrder, ...selectedNotInOrder, ...lockedNotInOrder];
      selectPopover.order = [...visibleFromOrder, ...selectedNotInOrder, ...unselected, ...lockedNotInOrder];
    } else {
      selected.sort(sortByDescription);
      unselected.sort(sortByDescription);
      disabled.sort(sortByDescription);
      selectPopover.order = [...selected, ...unselected, ...disabled];
      selectPopover.value = [...selected, ...disabled];
    }

    selectPopover.disabledKeys = disabled;

    return selectPopover;
  }

  static getColumnDefsFromLocalStorage(
    columns: ColDef[],
    storageKey: string,
    colDefOrder?: string[],
    isStockManager: boolean = false,
  ): ColDef[] {
    const storedOrder = colDefOrder || JSON.parse(localStorage.getItem(storageKey)) || [];
    const localColDefs = ensureDepartmentNameFields(storedOrder);
    if (
      storageKey &&
      storageKey !== 'ignoreStorageKeyHere' &&
      Array.isArray(localColDefs) &&
      JSON.stringify(localColDefs) !== JSON.stringify(storedOrder)
    ) {
      localStorage.setItem(storageKey, JSON.stringify(localColDefs));
    }
    if (!localColDefs || !Array.isArray(localColDefs) || localColDefs.length === 0) {
      if (columns?.length > 0) {
        return columns;
      }
      return isStockManager
        ? stockManagerGridColDefs
        : ngpReportGridColDefs;
    }

    const fixedOrder = isStockManager ? ['desc', 'code', 'stockId', 'icons'] : ['isSelected', 'diffGP', 'nominalGP', 'sellPriIncl1', 'desc', 'code', 'stockId', 'icons'];

    const colDefs = columns?.length > 0 ? [...columns] : (isStockManager ? [...stockManagerGridColDefs] : [...ngpReportGridColDefs]);

    colDefs.sort((a: ColDef, b: ColDef) => {
      const indexA = localColDefs.indexOf(a.field);
      const indexB = localColDefs.indexOf(b.field);
      return indexA - indexB;
    });

    colDefs.sort((a: ColDef, b: ColDef) => {
      const fixedIndexA = fixedOrder.indexOf(a.field);
      const fixedIndexB = fixedOrder.indexOf(b.field);
      if (fixedIndexA === -1 && fixedIndexB === -1) {
        return 0;
      }
      if (fixedIndexA === -1) {
        return 1;
      }
      if (fixedIndexB === -1) {
        return -1;
      }
      return fixedIndexB - fixedIndexA;
    });

    return colDefs;
  }


  static getVisibleFieldsFromLocalStorage(storageKey: string): string[] {
    const storedData = localStorage.getItem(storageKey);
    let visibleFields: string[] = stockMangerVisibleFieldsDefault;
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        if (parsedData.length > 0) {
          visibleFields = ensureDepartmentNameFields(parsedData);
          if (JSON.stringify(visibleFields) !== JSON.stringify(parsedData)) {
            localStorage.setItem(storageKey, JSON.stringify(visibleFields));
          }
        }
      } catch (error) {
        console.error(`Error parsing JSON from localStorage for key ${storageKey}:`, error);
      }
    }
    return visibleFields;
  }

  static getContrastColor(hexColor: string): string {
    const rgb = GridUtils.hexToRgb(hexColor);
    const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
    return brightness > 128 ? 'black' : 'white';
  }


  static hexToRgb(hex: string): { r: number; g: number; b: number } {
    hex = hex.replace(/^#/, '');
    if (hex.length === 3) {
      hex = hex.split('').map(char => char + char).join('');
    }

    const bigint = parseInt(hex, 16);
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255,
    };
  }

}
